import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  FileText,
  Cpu,
  Zap,
  Download,
  CheckCircle,
  Loader2,
  LayoutTemplate,
  Briefcase,
  Upload,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  FileCheck,
  Clock,
  Users,
  DollarSign,
  Target,
  Award,
  FolderOpen,
  Eye,
  History,
  Folder,
  FileType,
  Presentation,
  X,
  BarChart3,
  PieChart,
  TrendingUp,
  Settings,
  GripVertical,
  ToggleLeft,
  ToggleRight,
  ClipboardList,
  Play,
  Sparkles,
  Shield,
  MessageSquare,
  AlertTriangle,
  RefreshCw,
  BookOpen
} from 'lucide-react';
import McKinseySlidePreview from './components/McKinseySlidePreview';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// McKinsey color constants
const MCKINSEY_COLORS = {
  navy: '#00274C',
  blue: '#005293',
  lightBlue: '#007BC1',
  dark: '#262626',
  gray: '#666666',
  lightGray: '#F2F2F2',
  white: '#FFFFFF'
};

// ==================== COMPONENTS ====================

const Button = ({ children, variant = 'primary', className = '', ...props }) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: 'bg-[#00274C] text-white hover:bg-[#005293] h-10 px-6 shadow-lg hover:shadow-xl',
    secondary: 'bg-[#F2F2F2] text-[#262626] hover:bg-[#E5E5E5] h-10 px-6 border border-[#D0D0D0]',
    ghost: 'hover:bg-[#F2F2F2] text-[#262626] h-10 px-4',
    outline: 'border-2 border-[#00274C] text-[#00274C] hover:bg-[#00274C] hover:text-white h-10 px-4'
  };
  
  return (
    <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Card = ({ children, className = '', interactive = false, ...props }) => {
  const baseStyles = 'bg-white text-[#262626] rounded-lg border border-[#E5E5E5] shadow-sm p-6 transition-all duration-300';
  const interactiveStyles = interactive ? 'cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-[#007BC1]' : '';
  
  return (
    <div className={`${baseStyles} ${interactiveStyles} ${className}`} {...props}>
      {children}
    </div>
  );
};

const Progress = ({ value, className = '' }) => (
  <div className={`w-full bg-[#F2F2F2] rounded-full h-2 overflow-hidden ${className}`}>
    <div className="h-full bg-[#007BC1] progress-fill rounded-full" style={{ width: `${value}%` }} />
  </div>
);

// SlidePreview is now imported from McKinseySlidePreview component

// ==================== HOME PAGE ====================

const HomePage = ({ onUpload, isUploading, onViewSaved }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) onUpload(acceptedFiles[0]);
  }, [onUpload]);
  
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    noClick: true,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false)
  });
  
  const features = [
    { icon: BarChart3, title: 'Strategic Frameworks', description: 'MECE analysis, Pyramid Principle, McKinsey 7S, Issue Trees' },
    { icon: PieChart, title: 'Visual Excellence', description: 'Charts, tables, KPI dashboards, and professional 16:9 slides' },
    { icon: TrendingUp, title: 'Executive-Ready', description: 'Quantified insights, ROI projections, and C-suite presentations' }
  ];
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="relative overflow-hidden">
        {/* Hero gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00274C]/5 via-transparent to-[#007BC1]/5" />
        
        <div className="relative container mx-auto px-4 py-16 md:py-24 max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00274C]/10 border border-[#00274C]/20 mb-6">
              <Briefcase className="w-4 h-4 text-[#00274C]" />
              <span className="text-sm text-[#00274C] font-medium">McKinsey-Level Quality</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#00274C] mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Consulting Proposal<br />
              <span className="text-[#007BC1]">Generator</span>
            </h1>
            
            <p className="text-lg md:text-xl text-[#666666] max-w-2xl mx-auto">
              Transform RFPs into executive-ready proposals and create Project Execution Guideline for our Protiviti Consultants
            </p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="max-w-2xl mx-auto mb-8">
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer bg-white transition-all ${
                isDragging ? 'border-[#007BC1] bg-[#007BC1]/5 scale-[1.02]' : 'border-[#D0D0D0] hover:border-[#007BC1]'
              }`}
              data-testid="upload-dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              
              {isUploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-12 h-12 text-[#007BC1] animate-spin" />
                  <p className="text-[#666666]">Processing document...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#00274C]/10 flex items-center justify-center mx-auto mb-6">
                    <Upload className="w-8 h-8 text-[#00274C]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#262626] mb-2">Drop your RFP here</h3>
                  <p className="text-[#666666] mb-4">PDF, DOCX, TXT supported</p>
                  <Button onClick={open} data-testid="browse-files-btn">Browse Files</Button>
                </>
              )}
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-center mb-12">
            <Button variant="outline" onClick={onViewSaved} data-testid="view-saved-btn">
              <History className="w-4 h-4 mr-2" />
              View Saved Proposals
            </Button>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center" data-testid={`feature-card-${index}`}>
                <div className="w-12 h-12 rounded-lg bg-[#00274C]/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-[#00274C]" />
                </div>
                <h3 className="text-lg font-semibold text-[#262626] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#666666]">{feature.description}</p>
              </Card>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// ==================== PREVIEW MODE / CUSTOMIZATION PAGE ====================

const DEFAULT_SECTIONS = [
  { 
    id: 'executive_summary', 
    name: 'Executive Summary', 
    description: 'Strategic overview and value proposition',
    icon: FileText, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 40
  },
  { 
    id: 'understanding', 
    name: 'Understanding of Requirements', 
    description: 'Client needs analysis and scope definition',
    icon: Target, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 35
  },
  { 
    id: 'solution', 
    name: 'Proposed Solution', 
    description: 'Methodology and approach framework',
    icon: LayoutTemplate, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 50
  },
  { 
    id: 'project_plan', 
    name: 'Project Plan & Timeline', 
    description: 'Detailed schedule and milestones',
    icon: Clock, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 40
  },
  { 
    id: 'team', 
    name: 'Team Structure', 
    description: 'Key personnel and capabilities',
    icon: Users, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 30
  },
  { 
    id: 'pricing', 
    name: 'Pricing & Commercial Terms', 
    description: 'Investment breakdown and payment terms',
    icon: DollarSign, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 35
  },
  { 
    id: 'differentiators', 
    name: 'Differentiators', 
    description: 'Why choose us - competitive advantages',
    icon: Award, 
    enabled: true, 
    type: 'proposal',
    estimatedTime: 30
  },
  { 
    id: 'project_execution_guideline', 
    name: 'Project Execution Guideline', 
    description: '500-word RFP-specific execution manual',
    icon: ClipboardList, 
    enabled: true, 
    type: 'proposal',
    isNew: true,
    isDocument: true,
    estimatedTime: 45
  }
];

const DEFAULT_DELIVERABLES = [
  { 
    id: 'current_state', 
    name: 'Current State Assessment', 
    description: '7S Analysis, benchmarking, gap analysis',
    icon: BarChart3, 
    enabled: true, 
    type: 'deliverable',
    estimatedTime: 50
  },
  { 
    id: 'strategic_roadmap', 
    name: 'Strategic Roadmap', 
    description: '3 Horizons framework, initiative planning',
    icon: TrendingUp, 
    enabled: true, 
    type: 'deliverable',
    estimatedTime: 45
  },
  { 
    id: 'implementation_plan', 
    name: 'Implementation Plan', 
    description: 'Workstream plans, resource allocation',
    icon: Settings, 
    enabled: true, 
    type: 'deliverable',
    estimatedTime: 55
  },
  { 
    id: 'risk_assessment', 
    name: 'Risk Assessment', 
    description: 'Risk matrix, mitigation strategies',
    icon: AlertTriangle, 
    enabled: true, 
    type: 'deliverable',
    estimatedTime: 40
  },
  { 
    id: 'project_charter', 
    name: 'Project Charter', 
    description: 'Scope, objectives, success criteria',
    icon: BookOpen, 
    enabled: true, 
    type: 'deliverable',
    estimatedTime: 35
  }
];

const PreviewModePage = ({ rfpData, onStartGeneration, onBack }) => {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [deliverables, setDeliverables] = useState(DEFAULT_DELIVERABLES);
  const [draggedItem, setDraggedItem] = useState(null);
  
  const enabledSections = sections.filter(s => s.enabled);
  const enabledDeliverables = deliverables.filter(d => d.enabled);
  
  const totalEstimatedTime = [...enabledSections, ...enabledDeliverables].reduce((acc, item) => acc + item.estimatedTime, 0);
  const totalDocuments = enabledSections.length + enabledDeliverables.length;
  
  const toggleSection = (id) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  };
  
  const toggleDeliverable = (id) => {
    setDeliverables(prev => prev.map(d => d.id === id ? { ...d, enabled: !d.enabled } : d));
  };
  
  const moveSection = (fromIndex, toIndex) => {
    setSections(prev => {
      const newSections = [...prev];
      const [removed] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, removed);
      return newSections;
    });
  };
  
  const handleDragStart = (e, index, type) => {
    setDraggedItem({ index, type });
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index, type) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type === type && draggedItem.index !== index) {
      if (type === 'section') {
        moveSection(draggedItem.index, index);
        setDraggedItem({ index, type });
      }
    }
  };
  
  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={onBack} data-testid="preview-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-[#00274C]" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Customize Your Proposal
            </h1>
            <p className="text-[#666666]">{rfpData?.filename}</p>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="text-center" data-testid="summary-documents">
            <div className="w-12 h-12 rounded-full bg-[#007BC1]/10 flex items-center justify-center mx-auto mb-3">
              <FileCheck className="w-6 h-6 text-[#007BC1]" />
            </div>
            <p className="text-3xl font-bold text-[#00274C]">{totalDocuments}</p>
            <p className="text-sm text-[#666666]">Documents to Generate</p>
          </Card>
          
          <Card className="text-center" data-testid="summary-time">
            <div className="w-12 h-12 rounded-full bg-[#00274C]/10 flex items-center justify-center mx-auto mb-3">
              <Clock className="w-6 h-6 text-[#00274C]" />
            </div>
            <p className="text-3xl font-bold text-[#00274C]">{formatTime(totalEstimatedTime)}</p>
            <p className="text-sm text-[#666666]">Estimated Time</p>
          </Card>
          
          <Card className="text-center" data-testid="summary-quality">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">MBB</p>
            <p className="text-sm text-[#666666]">Quality Standard</p>
          </Card>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Proposal Sections */}
          <Card data-testid="sections-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#00274C]">Proposal Sections</h2>
              <span className="text-sm text-[#666666]">{enabledSections.length} selected</span>
            </div>
            
            <p className="text-sm text-[#666666] mb-4">Drag to reorder • Toggle to include/exclude</p>
            
            <div className="space-y-2">
              {sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  layout
                  draggable
                  onDragStart={(e) => handleDragStart(e, index, 'section')}
                  onDragOver={(e) => handleDragOver(e, index, 'section')}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${
                    section.enabled 
                      ? 'bg-white border-[#007BC1]/30 shadow-sm' 
                      : 'bg-[#F8F9FA] border-transparent opacity-60'
                  } ${section.isNew ? 'ring-2 ring-[#007BC1]/30' : ''}`}
                  data-testid={`section-${section.id}`}
                >
                  <GripVertical className="w-4 h-4 text-[#999999] flex-shrink-0" />
                  
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    section.enabled ? 'bg-[#007BC1]/10' : 'bg-[#E5E5E5]'
                  }`}>
                    <section.icon className={`w-5 h-5 ${section.enabled ? 'text-[#007BC1]' : 'text-[#999999]'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-medium text-sm ${section.enabled ? 'text-[#262626]' : 'text-[#999999]'}`}>
                        {section.name}
                      </h4>
                      {section.isNew && (
                        <span className="px-2 py-0.5 rounded-full bg-[#007BC1] text-white text-xs font-medium">
                          NEW
                        </span>
                      )}
                      {section.isDocument && (
                        <span className="px-2 py-0.5 rounded-full bg-[#00274C] text-white text-xs font-medium">
                          DOC
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#666666] truncate">{section.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#999999]">~{formatTime(section.estimatedTime)}</span>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="p-1 rounded hover:bg-[#F2F2F2]"
                      data-testid={`toggle-${section.id}`}
                    >
                      {section.enabled ? (
                        <ToggleRight className="w-6 h-6 text-[#007BC1]" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-[#999999]" />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
          
          {/* Deliverables */}
          <Card data-testid="deliverables-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#00274C]">Project Deliverables</h2>
              <span className="text-sm text-[#666666]">{enabledDeliverables.length} selected</span>
            </div>
            
            <p className="text-sm text-[#666666] mb-4">Additional RFP-specific documents</p>
            
            <div className="space-y-2">
              {deliverables.map((deliverable) => (
                <div
                  key={deliverable.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    deliverable.enabled 
                      ? 'bg-white border-[#00274C]/20 shadow-sm' 
                      : 'bg-[#F8F9FA] border-transparent opacity-60'
                  }`}
                  data-testid={`deliverable-${deliverable.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    deliverable.enabled ? 'bg-[#00274C]/10' : 'bg-[#E5E5E5]'
                  }`}>
                    <deliverable.icon className={`w-5 h-5 ${deliverable.enabled ? 'text-[#00274C]' : 'text-[#999999]'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm ${deliverable.enabled ? 'text-[#262626]' : 'text-[#999999]'}`}>
                      {deliverable.name}
                    </h4>
                    <p className="text-xs text-[#666666] truncate">{deliverable.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#999999]">~{formatTime(deliverable.estimatedTime)}</span>
                    <button
                      onClick={() => toggleDeliverable(deliverable.id)}
                      className="p-1 rounded hover:bg-[#F2F2F2]"
                      data-testid={`toggle-deliverable-${deliverable.id}`}
                    >
                      {deliverable.enabled ? (
                        <ToggleRight className="w-6 h-6 text-[#00274C]" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-[#999999]" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
        
        {/* Generate Button */}
        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button 
            onClick={() => onStartGeneration(enabledSections, enabledDeliverables)}
            className="h-14 px-8 text-lg"
            disabled={enabledSections.length === 0}
            data-testid="start-generation-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Generate {totalDocuments} Documents
            <span className="ml-2 px-2 py-0.5 rounded bg-white/20 text-sm">
              ~{formatTime(totalEstimatedTime)}
            </span>
          </Button>
          
          <p className="text-sm text-[#666666] mt-3">
            Each document will be generated in PDF, PPTX, and Markdown formats
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// ==================== DOCUMENT CARD ====================

const DocumentCard = ({ document, folderName, onPreview }) => {
  const handleDownload = (fileType) => {
    const filename = document.files[fileType];
    if (filename) {
      window.open(`${API}/download/${folderName}/${filename}`, '_blank');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white border border-[#007BC1]/30 rounded-lg p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#007BC1]/10 flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-5 h-5 text-[#007BC1]" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[#262626] text-sm truncate">{document.name}</h4>
          <p className="text-xs text-[#007BC1] mt-1">Ready for download</p>
        </div>
      </div>
      
      <div className="flex gap-2 mt-3">
        <Button variant="outline" className="flex-1 h-8 text-xs" onClick={() => onPreview(document)} data-testid={`preview-${document.id}`}>
          <Eye className="w-3 h-3 mr-1" /> Preview
        </Button>
        <Button variant="secondary" className="h-8 px-3" onClick={() => handleDownload('pdf')} data-testid={`pdf-${document.id}`}>
          <FileType className="w-3 h-3" />
        </Button>
        <Button variant="secondary" className="h-8 px-3" onClick={() => handleDownload('ppt')} data-testid={`ppt-${document.id}`}>
          <Presentation className="w-3 h-3" />
        </Button>
      </div>
    </motion.div>
  );
};

// ==================== ANALYSIS PAGE ====================

const AnalysisPage = ({ rfpId, rfpData, onComplete, onBack, customSections, customDeliverables }) => {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [proposalId, setProposalId] = useState(null);
  const [folderPath, setFolderPath] = useState('');
  const [folderName, setFolderName] = useState('');
  const [readyDocuments, setReadyDocuments] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const logContainerRef = useRef(null);
  
  // Updated stages with Project Execution Guideline at the END
  const stages = [
    { key: 'executive_summary', label: 'Executive Summary', icon: FileText },
    { key: 'understanding', label: 'Requirements', icon: Target },
    { key: 'solution', label: 'Solution Design', icon: LayoutTemplate },
    { key: 'project_plan', label: 'Project Plan', icon: Clock },
    { key: 'team', label: 'Team Structure', icon: Users },
    { key: 'pricing', label: 'Pricing', icon: DollarSign },
    { key: 'differentiators', label: 'Differentiators', icon: Award },
    { key: 'deliverables', label: 'Deliverables', icon: FileCheck },
    { key: 'project_execution_guideline', label: 'Execution Guideline', icon: ClipboardList, isNew: true }
  ];
  
  useEffect(() => {
    const startGeneration = async () => {
      try {
        const eventSource = new EventSource(`${API}/generate/${rfpId}/stream`);
        
        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          
          if (data.type === 'start') {
            setProposalId(data.proposal_id);
            setFolderPath(data.folder_path || '');
            setFolderName(data.folder_path?.split('/').pop() || '');
            addLog('info', 'Starting McKinsey-quality proposal generation...');
          } else if (data.type === 'log') {
            setCurrentStage(data.stage);
            setProgress(data.progress);
            addLog(data.stage, data.message);
          } else if (data.type === 'document_ready') {
            setReadyDocuments(prev => [...prev, data.document]);
            addLog('ready', `✓ ${data.document.name} ready`);
            setProgress(data.progress);
          } else if (data.type === 'error') {
            addLog('error', `Error: ${data.message}`);
          } else if (data.type === 'complete') {
            setIsComplete(true);
            setProgress(100);
            addLog('complete', `All ${data.document_count} documents generated!`);
            eventSource.close();
          }
        };
        
        eventSource.onerror = () => {
          setError('Connection lost. Please try again.');
          eventSource.close();
        };
      } catch (err) {
        setError('Failed to start generation.');
      }
    };
    
    startGeneration();
  }, [rfpId]);
  
  const addLog = (stage, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, stage, message }]);
  };
  
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);
  
  const getStageStatus = (stageKey) => {
    const completedSections = readyDocuments.filter(d => d.type === 'proposal_section').map(d => d.section_key);
    const hasDeliverables = readyDocuments.some(d => d.type === 'deliverable');
    
    if (stageKey === 'deliverables') {
      if (isComplete) return 'complete';
      if (hasDeliverables) return 'active';
      if (completedSections.length === 8) return 'active'; // Updated to 8 sections
      return 'pending';
    }
    
    if (completedSections.includes(stageKey)) return 'complete';
    if (currentStage === stageKey) return 'active';
    return 'pending';
  };
  
  const handlePreview = (doc) => setPreviewDoc(doc);
  
  const handleDownloadPDF = () => {
    if (previewDoc?.files?.pdf && folderName) {
      window.open(`${API}/download/${folderName}/${previewDoc.files.pdf}`, '_blank');
    }
  };
  
  const handleDownloadPPT = () => {
    if (previewDoc?.files?.ppt && folderName) {
      window.open(`${API}/download/${folderName}/${previewDoc.files.ppt}`, '_blank');
    }
  };
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-[#00274C]" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Generating McKinsey Proposal
            </h1>
            <p className="text-[#666666]">{rfpData?.filename}</p>
          </div>
        </div>
        
        <Card className="mb-6" data-testid="progress-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#666666]">Overall Progress</span>
            <span className="text-sm font-bold text-[#007BC1]">{progress}%</span>
          </div>
          <Progress value={progress} />
          <p className="text-xs text-[#666666] mt-2">{readyDocuments.length} documents ready for download</p>
        </Card>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Progress Timeline */}
          <Card data-testid="timeline-card">
            <h2 className="text-lg font-semibold text-[#00274C] mb-4">Progress</h2>
            <div className="space-y-3">
              {stages.map((stage) => {
                const status = getStageStatus(stage.key);
                return (
                  <div key={stage.key} className="flex items-center gap-3" data-testid={`stage-${stage.key}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      status === 'complete' ? 'bg-[#007BC1]/20 text-[#007BC1]' :
                      status === 'active' ? 'bg-[#00274C]/20 text-[#00274C] animate-pulse' :
                      'bg-[#F2F2F2] text-[#666666]'
                    }`}>
                      {status === 'complete' ? <CheckCircle className="w-4 h-4" /> :
                       status === 'active' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                       <stage.icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <span className={`text-sm ${
                        status === 'complete' ? 'text-[#007BC1] font-medium' :
                        status === 'active' ? 'text-[#00274C] font-medium' : 'text-[#666666]'
                      }`}>{stage.label}</span>
                      {stage.isNew && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#007BC1] text-white text-[10px] font-bold">
                          NEW
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
          
          {/* Ready Documents */}
          <Card className="lg:col-span-2" data-testid="documents-card">
            <h2 className="text-lg font-semibold text-[#00274C] mb-4">
              Ready Documents ({readyDocuments.length})
            </h2>
            
            {readyDocuments.length === 0 ? (
              <div className="text-center py-8 text-[#666666]">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#007BC1]" />
                <p>Documents will appear here as they complete...</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                {readyDocuments.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    folderName={folderName}
                    onPreview={handlePreview}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
        
        {/* Log */}
        <Card className="mt-6" data-testid="log-card">
          <h2 className="text-sm font-semibold text-[#666666] mb-3">Generation Log</h2>
          <div ref={logContainerRef} className="bg-[#1a1a1a] h-32 overflow-y-auto rounded-lg p-3 font-mono text-xs">
            {logs.map((log, index) => (
              <div key={index} className="py-0.5">
                <span className="text-[#007BC1]">[{log.timestamp}]</span>{' '}
                <span className={log.stage === 'ready' ? 'text-green-400' : log.stage === 'error' ? 'text-red-400' : 'text-gray-400'}>
                  [{log.stage}]
                </span>{' '}
                <span className="text-gray-300">{log.message}</span>
              </div>
            ))}
          </div>
        </Card>
        
        {error && (
          <Card className="mt-6 border-red-300 bg-red-50">
            <p className="text-red-600">{error}</p>
          </Card>
        )}
        
        {isComplete && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
            <Card className="text-center border-[#007BC1]/30 bg-[#007BC1]/5">
              <CheckCircle className="w-12 h-12 text-[#007BC1] mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#00274C] mb-2">All Documents Ready!</h2>
              <p className="text-[#666666] mb-4">{readyDocuments.length} McKinsey-quality documents generated</p>
              <Button onClick={() => onComplete(proposalId, folderName)} data-testid="view-all-btn">
                View All Documents <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          </motion.div>
        )}
        
        {/* Slide Preview Modal */}
        <AnimatePresence>
          {previewDoc && (
            <McKinseySlidePreview
              content={previewDoc.content || ''}
              title={previewDoc.name}
              onClose={() => setPreviewDoc(null)}
              onDownloadPDF={handleDownloadPDF}
              onDownloadPPT={handleDownloadPPT}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ==================== SAVED PROPOSALS PAGE ====================

const SavedProposalsPage = ({ onBack, onSelectFolder }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    axios.get(`${API}/saved-proposals`).then(res => setFolders(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);
  
  if (loading) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#007BC1] animate-spin" /></div>;
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          <div>
            <h1 className="text-2xl font-bold text-[#00274C]" style={{ fontFamily: 'Barlow, sans-serif' }}>Saved Proposals</h1>
            <p className="text-[#666666]">Browse generated proposals</p>
          </div>
        </div>
        
        {folders.length === 0 ? (
          <Card className="text-center py-12">
            <FolderOpen className="w-12 h-12 text-[#666666] mx-auto mb-4" />
            <p className="text-[#666666]">No saved proposals yet.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {folders.map((folder, index) => (
              <motion.div key={folder.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                <Card interactive onClick={() => onSelectFolder(folder.name)}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#00274C]/10 flex items-center justify-center">
                      <Folder className="w-6 h-6 text-[#00274C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#262626] mb-1 truncate">{folder.name}</h3>
                      <p className="text-sm text-[#666666]">{folder.file_count} files</p>
                      <p className="text-xs text-[#666666] mt-1">{new Date(folder.created).toLocaleDateString()}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== FOLDER VIEWER ====================

const FolderViewerPage = ({ folderName, onBack }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/saved-proposals/${folderName}/files`).then(res => setFiles(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [folderName]);
  
  const handleFileClick = async (file) => {
    if (['pdf', 'pptx'].includes(file.type)) {
      window.open(`${API}/download/${folderName}/${file.name}`, '_blank');
      return;
    }
    
    try {
      const res = await axios.get(`${API}/saved-proposals/${folderName}/file/${file.name}`);
      setPreviewContent(res.data.content);
      setPreviewDoc({
        name: file.name.replace('.md', ''),
        content: res.data.content,
        files: {
          pdf: file.name.replace('.md', '.pdf'),
          ppt: file.name.replace('.md', '.pptx')
        }
      });
    } catch { 
      setPreviewContent('Error loading file'); 
    }
  };
  
  const getFileIcon = (type) => {
    if (type === 'pdf') return FileType;
    if (type === 'pptx') return Presentation;
    return FileText;
  };
  
  if (loading) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#007BC1] animate-spin" /></div>;
  
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          <div>
            <h1 className="text-2xl font-bold text-[#00274C]" style={{ fontFamily: 'Barlow, sans-serif' }}>{folderName}</h1>
            <p className="text-[#666666]">{files.length} files</p>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-4 gap-6">
          <Card className="lg:col-span-1">
            <h3 className="text-sm font-semibold text-[#666666] uppercase mb-4">Files</h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {files.map((file) => {
                const Icon = getFileIcon(file.type);
                return (
                  <button
                    key={file.name}
                    onClick={() => handleFileClick(file)}
                    className="w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 hover:bg-[#F2F2F2] border border-transparent hover:border-[#007BC1]/30"
                  >
                    <Icon className={`w-5 h-5 ${file.type === 'pdf' ? 'text-red-500' : file.type === 'pptx' ? 'text-orange-500' : 'text-[#007BC1]'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#262626] truncate">{file.name}</p>
                    </div>
                    {['pdf', 'pptx'].includes(file.type) && <Download className="w-4 h-4 text-[#666666]" />}
                  </button>
                );
              })}
            </div>
          </Card>
          
          <Card className="lg:col-span-3">
            <div className="text-center py-12 text-[#666666]">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Click a .md file to preview as slides</p>
              <p className="text-sm mt-2">PDF/PPT files will download directly</p>
            </div>
          </Card>
        </div>
        
        {/* Slide Preview Modal */}
        <AnimatePresence>
          {previewDoc && (
            <McKinseySlidePreview
              content={previewDoc.content || previewContent}
              title={previewDoc.name}
              onClose={() => setPreviewDoc(null)}
              onDownloadPDF={() => window.open(`${API}/download/${folderName}/${previewDoc.files.pdf}`, '_blank')}
              onDownloadPPT={() => window.open(`${API}/download/${folderName}/${previewDoc.files.ppt}`, '_blank')}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================

function App() {
  const [view, setView] = useState('home');
  const [rfpId, setRfpId] = useState(null);
  const [rfpData, setRfpData] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [customSections, setCustomSections] = useState(null);
  const [customDeliverables, setCustomDeliverables] = useState(null);
  
  const handleUpload = async (file) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRfpId(response.data.id);
      setRfpData(response.data);
      setView('preview'); // Go to preview mode first
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload file.');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleStartGeneration = (sections, deliverables) => {
    setCustomSections(sections);
    setCustomDeliverables(deliverables);
    setView('analysis');
  };
  
  const handleGenerationComplete = (newProposalId, folderName) => {
    setProposalId(newProposalId);
    setSelectedFolder(folderName);
    setView('folder');
  };
  
  const handleBackToHome = () => {
    setView('home');
    setRfpId(null);
    setRfpData(null);
    setProposalId(null);
    setSelectedFolder(null);
    setCustomSections(null);
    setCustomDeliverables(null);
  };
  
  return (
    <div className="App" data-testid="app-container">
      <link href="https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap" rel="stylesheet" />
      
      <AnimatePresence mode="wait">
        {view === 'home' && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <HomePage onUpload={handleUpload} isUploading={isUploading} onViewSaved={() => setView('saved')} />
          </motion.div>
        )}
        
        {view === 'preview' && rfpId && (
          <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <PreviewModePage 
              rfpData={rfpData} 
              onStartGeneration={handleStartGeneration} 
              onBack={handleBackToHome} 
            />
          </motion.div>
        )}
        
        {view === 'saved' && (
          <motion.div key="saved" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <SavedProposalsPage onBack={handleBackToHome} onSelectFolder={(folder) => { setSelectedFolder(folder); setView('folder'); }} />
          </motion.div>
        )}
        
        {view === 'folder' && selectedFolder && (
          <motion.div key="folder" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <FolderViewerPage folderName={selectedFolder} onBack={() => setView('saved')} />
          </motion.div>
        )}
        
        {view === 'analysis' && rfpId && (
          <motion.div key="analysis" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AnalysisPage 
              rfpId={rfpId} 
              rfpData={rfpData} 
              onComplete={handleGenerationComplete} 
              onBack={handleBackToHome}
              customSections={customSections}
              customDeliverables={customDeliverables}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
